
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  MessageSquare,
  BarChart3,
  Users,
  Globe,
  GraduationCap,
  BookOpen,
  RefreshCcw
} from 'lucide-react';
import Sidebar from '../../components/Sidebar';
import MobileBottomNav from '../../components/MobileBottomNav';

const VIEWBOX_WIDTH = 600;
const VIEWBOX_HEIGHT = 320;

const DashboardPage = () => {
  const navigate = useNavigate();

  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [isTablet, setIsTablet] = useState(false);

  const [loading, setLoading] = useState(true);
  const [softError, setSoftError] = useState('');

  const [cards, setCards] = useState({
    commentsCount: 0,
    commentsDeltaPct: 0,
    evaluationsCount: 0,
    evaluationsDeltaPct: 0,
    usersCount: 0,
    usersDeltaPct: 0,
    lecturersCount: 0,
    coursesCount: 0,
    pageViews: 0,
    uniquePages: 0,
    uniqueVisitors: 0,
    newVisitors: 0,
    returningVisitors: 0,
    onlineNow: 0,
    lastActivity: null
  });

  const [chartData, setChartData] = useState({
    instructorEvaluations: [],
    courseEvaluations: [],
    siteActivity: []
  });

  const [timeRange, setTimeRange] = useState('week');

  const [activeSeries, setActiveSeries] = useState({
    activity: true,
    instructors: true,
    courses: true
  });

  const [chartHoverIdx, setChartHoverIdx] = useState(null);

  const refreshTimerRef = useRef(null);

  const n = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0);

  const formatTimeAgo = (iso) => {
    try {
      if (!iso) return '';
      const then = new Date(iso);
      const now = new Date();
      const diffMs = now - then;
      if (!isFinite(diffMs)) return '';
      const minutes = Math.floor(diffMs / 60000);
      if (minutes < 0) return '';
      if (minutes < 1) return 'الآن';
      if (minutes < 60) {
        if (minutes === 1) return 'قبل دقيقة';
        if (minutes === 2) return 'قبل دقيقتين';
        if (minutes <= 10) return `قبل ${minutes} دقايق`;
        return `قبل ${minutes} دقيقة`;
      }

      const hours = Math.floor(minutes / 60);
      const days = Math.floor(hours / 24);
      const months = Math.floor(days / 30);
      const years = Math.floor(days / 365);

      if (hours < 24) {
        if (hours === 1) return 'قبل ساعة';
        if (hours === 2) return 'قبل ساعتين';
        if (hours <= 10) return `قبل ${hours} ساعات`;
        return `قبل ${hours} ساعة`;
      }

      if (days < 31) {
        if (days === 1) return 'قبل يوم';
        if (days === 2) return 'قبل يومين';
        if (days <= 10) return `قبل ${days} أيام`;
        return `قبل ${days} يوم`;
      }

      if (months < 12) {
        if (months === 1) return 'قبل شهر';
        if (months === 2) return 'قبل شهرين';
        if (months <= 10) return `قبل ${months} شهور`;
        return `قبل ${months} شهر`;
      }

      if (years === 1) return 'مضى سنة';
      if (years === 2) return 'مضى سنتين';
      if (years <= 10) return `مضى ${years} سنوات`;
      return 'مضى أكثر من 10 سنوات';
    } catch {
      return '';
    }
  };

  const generateMockData = (range) => {
    const base = {
      week: {
        instructorEvaluations: [
          { x: 0, y: 220, value: 42 },
          { x: 100, y: 210, value: 45 },
          { x: 200, y: 190, value: 51 },
          { x: 300, y: 160, value: 63 },
          { x: 400, y: 150, value: 68 },
          { x: 500, y: 135, value: 72 },
          { x: 600, y: 140, value: 70 }
        ],
        courseEvaluations: [
          { x: 0, y: 250, value: 61 },
          { x: 100, y: 230, value: 69 },
          { x: 200, y: 210, value: 75 },
          { x: 300, y: 200, value: 78 },
          { x: 400, y: 190, value: 82 },
          { x: 500, y: 180, value: 86 },
          { x: 600, y: 175, value: 88 }
        ],
        siteActivity: [
          { x: 0, y: 70, value: 120 },
          { x: 100, y: 120, value: 95 },
          { x: 200, y: 80, value: 112 },
          { x: 300, y: 160, value: 72 },
          { x: 400, y: 130, value: 84 },
          { x: 500, y: 190, value: 58 },
          { x: 600, y: 150, value: 76 }
        ]
      },
      month: {
        instructorEvaluations: [
          { x: 0, y: 230, value: 40 },
          { x: 100, y: 220, value: 44 },
          { x: 200, y: 210, value: 47 },
          { x: 300, y: 190, value: 55 },
          { x: 400, y: 180, value: 60 },
          { x: 500, y: 170, value: 64 },
          { x: 600, y: 175, value: 62 }
        ],
        courseEvaluations: [
          { x: 0, y: 260, value: 58 },
          { x: 100, y: 240, value: 66 },
          { x: 200, y: 230, value: 70 },
          { x: 300, y: 220, value: 74 },
          { x: 400, y: 210, value: 78 },
          { x: 500, y: 200, value: 82 },
          { x: 600, y: 195, value: 84 }
        ],
        siteActivity: [
          { x: 0, y: 60, value: 130 },
          { x: 100, y: 90, value: 118 },
          { x: 200, y: 110, value: 105 },
          { x: 300, y: 130, value: 92 },
          { x: 400, y: 160, value: 76 },
          { x: 500, y: 190, value: 60 },
          { x: 600, y: 170, value: 68 }
        ]
      },
      quarter: {
        instructorEvaluations: [
          { x: 0, y: 240, value: 38 },
          { x: 100, y: 230, value: 41 },
          { x: 200, y: 220, value: 45 },
          { x: 300, y: 210, value: 49 },
          { x: 400, y: 205, value: 50 },
          { x: 500, y: 200, value: 52 },
          { x: 600, y: 198, value: 53 }
        ],
        courseEvaluations: [
          { x: 0, y: 270, value: 54 },
          { x: 100, y: 260, value: 57 },
          { x: 200, y: 250, value: 60 },
          { x: 300, y: 245, value: 62 },
          { x: 400, y: 240, value: 64 },
          { x: 500, y: 235, value: 66 },
          { x: 600, y: 230, value: 68 }
        ],
        siteActivity: [
          { x: 0, y: 50, value: 140 },
          { x: 100, y: 80, value: 126 },
          { x: 200, y: 120, value: 108 },
          { x: 300, y: 150, value: 92 },
          { x: 400, y: 180, value: 76 },
          { x: 500, y: 210, value: 60 },
          { x: 600, y: 200, value: 66 }
        ]
      },
      year: {
        instructorEvaluations: [
          { x: 0, y: 250, value: 35 },
          { x: 100, y: 245, value: 36 },
          { x: 200, y: 240, value: 38 },
          { x: 300, y: 235, value: 40 },
          { x: 400, y: 230, value: 42 },
          { x: 500, y: 228, value: 43 },
          { x: 600, y: 225, value: 44 }
        ],
        courseEvaluations: [
          { x: 0, y: 280, value: 50 },
          { x: 100, y: 275, value: 52 },
          { x: 200, y: 270, value: 54 },
          { x: 300, y: 265, value: 56 },
          { x: 400, y: 260, value: 58 },
          { x: 500, y: 255, value: 60 },
          { x: 600, y: 250, value: 62 }
        ],
        siteActivity: [
          { x: 0, y: 40, value: 150 },
          { x: 100, y: 70, value: 132 },
          { x: 200, y: 110, value: 112 },
          { x: 300, y: 150, value: 92 },
          { x: 400, y: 190, value: 70 },
          { x: 500, y: 210, value: 60 },
          { x: 600, y: 190, value: 70 }
        ]
      }
    };
    return base[range] || base.week;
  };

  const CPX = (x) => 60 + (x / 600) * 625;
  const CPY = (y) => 10 + (y / 320) * 300;
  const CHART_BOTTOM = 310;

  const smoothPath = (pts) => {
    if (!pts || pts.length < 2) return '';
    let d = `M${CPX(pts[0].x).toFixed(1)},${CPY(pts[0].y).toFixed(1)}`;
    for (let i = 1; i < pts.length; i++) {
      const p0 = pts[Math.max(0, i - 2)];
      const p1 = pts[i - 1];
      const p2 = pts[i];
      const p3 = pts[Math.min(pts.length - 1, i + 1)];
      const cp1x = CPX(p1.x) + (CPX(p2.x) - CPX(p0.x)) / 6;
      const cp1y = CPY(p1.y) + (CPY(p2.y) - CPY(p0.y)) / 6;
      const cp2x = CPX(p2.x) - (CPX(p3.x) - CPX(p1.x)) / 6;
      const cp2y = CPY(p2.y) - (CPY(p3.y) - CPY(p1.y)) / 6;
      d += ` C${cp1x.toFixed(1)},${cp1y.toFixed(1)} ${cp2x.toFixed(1)},${cp2y.toFixed(1)} ${CPX(p2.x).toFixed(1)},${CPY(p2.y).toFixed(1)}`;
    }
    return d;
  };

  const smoothArea = (pts) => {
    if (!pts || pts.length < 2) return '';
    return `${smoothPath(pts)} L${CPX(pts[pts.length-1].x).toFixed(1)},${CHART_BOTTOM} L${CPX(pts[0].x).toFixed(1)},${CHART_BOTTOM} Z`;
  };

  const xAxisLabels = (() => {
    if (timeRange === 'week')    return ['أحد','إثنين','ثلاثاء','أربعاء','خميس','جمعة','سبت'];
    if (timeRange === 'month')   return ['الأول','','الثاني','','الثالث','','الرابع'];
    if (timeRange === 'quarter') return ['شهر 1','','شهر 2','','شهر 3','','الآن'];
    return ['يناير','مارس','مايو','يوليو','سبتمبر','نوفمبر','الآن'];
  })();

  const updateChartWithRealData = async (range, opts = { silent: false }) => {
    const silent = !!opts?.silent;
    if (!silent) setLoading(true);
    setSoftError('');

    try {
      const res = await fetch(`/api/dashboard/evaluations?timeRange=${range}`, {
        method: 'GET',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' }
      });

      if (!res.ok) throw new Error(`HTTP ${res.status}`);

      const data = await res.json();
      const series = data.series || {};
      const apiCards = data.cards || {};

      setChartData({
        instructorEvaluations: Array.isArray(series.instructorEvaluations) ? series.instructorEvaluations : [],
        courseEvaluations: Array.isArray(series.courseEvaluations) ? series.courseEvaluations : [],
        siteActivity: Array.isArray(series.siteActivity) ? series.siteActivity : []
      });

      setCards((prev) => ({
        ...prev,
        commentsCount: n(apiCards.commentsCount),
        commentsDeltaPct: n(apiCards.commentsDeltaPct),
        evaluationsCount: n(apiCards.evaluationsCount),
        evaluationsDeltaPct: n(apiCards.evaluationsDeltaPct),
        usersCount: n(apiCards.usersCount),
        usersDeltaPct: n(apiCards.usersDeltaPct),
        lecturersCount: n(apiCards.lecturersCount),
        coursesCount: n(apiCards.coursesCount),
        pageViews: n(apiCards.pageViews),
        uniquePages: n(apiCards.uniquePages),
        uniqueVisitors: n(apiCards.uniqueVisitors),
        newVisitors: n(apiCards.newVisitors),
        returningVisitors: n(apiCards.returningVisitors),
        onlineNow: n(apiCards.onlineNow),
        lastActivity: apiCards.lastActivity || null
      }));
    } catch (err) {
      const mock = generateMockData(range);
      setChartData(mock);
      setSoftError('تعذر تحميل البيانات الحقيقية. تم عرض بيانات تجريبية مؤقتًا.');
    } finally {
      if (!silent) setLoading(false);
    }
  };

  const handleTimeRangeChange = (range) => {
    setTimeRange(range);
    updateChartWithRealData(range);
  };

  const lastActivityText = useMemo(
    () => (cards.lastActivity ? formatTimeAgo(cards.lastActivity) : ''),
    [cards.lastActivity]
  );

  useEffect(() => {
    updateChartWithRealData(timeRange);

    if (refreshTimerRef.current) clearInterval(refreshTimerRef.current);
    refreshTimerRef.current = setInterval(() => {
      updateChartWithRealData(timeRange, { silent: true });
    }, 60000);

    return () => {
      if (refreshTimerRef.current) clearInterval(refreshTimerRef.current);
    };
  }, []);

  useEffect(() => {
    const handleResize = () => {
      const w = window.innerWidth;
      setIsMobile(w < 768);
      setIsTablet(w >= 768 && w < 1024);
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const metricCardBase = {
    background: 'radial-gradient(55% 90% at 50% -5%,rgba(40,227,218,.18) 0%,rgba(40,227,218,.04) 55%,rgba(8,20,35,.9) 100%)',
    boxShadow: '0 4px 20px rgba(0,0,0,.3)',
    borderRadius: '18px',
    border: '1px solid rgba(40,227,218,.22)',
    padding: '16px',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    transition: 'transform .2s, box-shadow .2s',
    cursor: 'default',
    outline: 'none',
    backdropFilter: 'blur(6px)',
  };

  const bottomCardBase = {
    borderRadius: '20px',
    background: 'radial-gradient(55% 100% at 50% -5%,rgba(40,227,218,.14) 0%,rgba(40,227,218,0) 60%),rgba(9,28,43,.9)',
    border: '1px solid rgba(40,227,218,.22)',
    boxShadow: '0 8px 32px rgba(0,0,0,.35)',
    padding: isMobile ? '16px 14px' : '20px 22px',
    display: 'flex',
    flexDirection: 'column',
    gap: '14px',
    minHeight: isMobile ? '190px' : '210px',
    transition: 'transform .2s, box-shadow .2s',
    cursor: 'default',
    outline: 'none',
    backdropFilter: 'blur(6px)',
  };

  const onCardHoverIn = (e) => {
    e.currentTarget.style.transform = 'translateY(-4px)';
    e.currentTarget.style.boxShadow = '0 20px 60px rgba(0,0,0,.55)';
  };

  const onCardHoverOut = (e) => {
    e.currentTarget.style.transform = 'translateY(0px)';
    e.currentTarget.style.boxShadow = '0 18px 45px rgba(0,0,0,.45)';
  };

  const onBarHoverIn = (e) => {
    e.currentTarget.style.transform = 'translateY(-4px)';
  };

  const onBarHoverOut = (e) => {
    e.currentTarget.style.transform = 'translateY(0px)';
  };

  const pillBtn = (active) => ({
    padding: '6px 10px',
    borderRadius: '999px',
    border: active ? '1px solid rgba(40,227,218,0.9)' : '1px solid rgba(124,150,170,0.4)',
    background: active
      ? 'linear-gradient(135deg, rgba(40,227,218,0.95), rgba(15,82,99,0.9))'
      : 'rgba(7,22,36,0.7)',
    color: active ? '#041019' : '#D6E4F7',
    fontFamily: 'Alexandria',
    fontSize: '12px',
    fontWeight: active ? 700 : 600,
    cursor: 'pointer',
    transition: 'background .18s ease-out, transform .18s ease-out, opacity .18s ease-out',
    outline: 'none'
  });

  const iconChip = {
    width: 30,
    height: 30,
    borderRadius: '999px',
    background: 'rgba(8,20,35,0.75)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  };

  return (
    <div
      className="min-h-screen text-white"
      style={{
        direction: 'rtl',
        display: 'flex',
        flexDirection: 'row',
        background:
          'linear-gradient(0deg, rgba(255,255,255,0.02), rgba(255,255,255,0.02)), radial-gradient(92% 180% at 50% -55%, rgba(40,227,218,0.16) 0%, rgba(40,227,218,0) 100%), #081423'
      }}
    >
      <div className="hidden md:block" style={{ position: 'sticky', top: 0, height: '100vh', zIndex: 200, flexShrink: 0 }}>
        <Sidebar isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} />
      </div>

      <div className="md:hidden">
        <Sidebar isOpen={isSidebarOpen} onClose={() => setIsSidebarOpen(false)} />
      </div>

      <div className="flex-1 flex flex-col md:ml-0" style={{ minWidth: 0 }}>
        <div
          className="flex-1"
          style={{
            padding: isMobile ? '16px 12px 80px' : isTablet ? '20px 24px 24px' : '24px 48px 32px',
          }}
        >
          <nav style={{
            display: 'flex', alignItems: 'center', justifyContent: isMobile ? 'flex-start' : 'space-evenly',
            flexWrap: 'wrap', gap: isMobile ? '6px' : '4px',
            padding: isMobile ? '10px 12px' : '0 16px',
            height: isMobile ? 'auto' : '52px',
            background: 'radial-gradient(43.64% 77.08% at 50% -10.42%,rgba(40,227,218,.2) 0%,rgba(40,227,218,0) 100%),rgba(9,28,43,.6)',
            border: '1px solid rgba(40,227,218,.2)',
            borderRadius: '16px',
            backdropFilter: 'blur(4px)',
            marginBottom: '24px',
          }}>
            {[
              { label: 'نظرة عامة',           active: true },
              { label: 'إدارة المستخدمين',    path: '/user-management' },
              { label: 'سجل النشاط',          path: '/activity-log' },
              { label: 'الإبلاغات',           path: '/reports' },
              { label: 'إضافة مقرر / محاضر', path: '/add-course-lecturer' },
              { label: 'إدارة المقررات',      path: '/manage-courses-lecturers' },
            ].map((item, i) => (
              <React.Fragment key={item.label}>
                {i > 0 && !isMobile && <span style={{ color: 'rgba(255,255,255,.15)', fontSize: '18px', userSelect: 'none' }}>|</span>}
                <span
                  onClick={() => item.path && navigate(item.path)}
                  style={{
                    fontFamily: 'Alexandria,sans-serif', fontSize: isMobile ? '12px' : '14px',
                    fontWeight: item.active ? 800 : 500, color: item.active ? '#7DF9E8' : '#94a3b8',
                    cursor: item.path ? 'pointer' : 'default',
                    padding: isMobile ? '5px 10px' : '0 4px', borderRadius: isMobile ? '8px' : 0,
                    background: item.active && isMobile ? 'rgba(40,227,218,.1)' : 'transparent',
                    transition: 'color .15s', whiteSpace: 'nowrap',
                  }}
                  onMouseEnter={e => { if (item.path) e.currentTarget.style.color = '#fff'; }}
                  onMouseLeave={e => { if (item.path) e.currentTarget.style.color = '#94a3b8'; }}
                >{item.label}</span>
              </React.Fragment>
            ))}
          </nav>

          <div
            style={{
              width: '100%',
              maxWidth: '1108px',
              margin: '0 auto 14px',
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: '10px'
            }}
          >
            <div style={{ direction: 'rtl', fontFamily: 'Alexandria', fontSize: '12px', color: '#C9E7FF' }}>
              {softError ? (
                <span style={{ color: '#F6C94C' }}>{softError}</span>
              ) : (
                <span style={{ opacity: 0.85 }}>
                  {cards.lastActivity ? `آخر نشاط: ${lastActivityText}` : 'آخر نشاط غير متوفر'}
                </span>
              )}
            </div>

            <button
              type="button"
              onClick={() => updateChartWithRealData(timeRange)}
              disabled={loading}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '8px 12px',
                borderRadius: '999px',
                border: '1px solid rgba(40,227,218,0.35)',
                background: 'rgba(7,22,36,0.65)',
                color: '#D6E4F7',
                fontFamily: 'Alexandria',
                fontSize: '12px',
                fontWeight: 700,
                opacity: loading ? 0.6 : 1,
                cursor: loading ? 'not-allowed' : 'pointer',
                boxShadow: '0 10px 25px rgba(0,0,0,0.25)'
              }}
            >
              <RefreshCcw size={16} className={loading ? 'animate-spin' : ''} />
              تحديث الآن
            </button>
          </div>

          <div
            style={{
              display: 'grid',
              gridTemplateColumns: isMobile ? 'repeat(2,minmax(0,1fr))' : 'repeat(4,minmax(0,1fr))',
              gap: isMobile ? '14px' : '20px',
              width: '100%',
              maxWidth: '1108px',
              margin: '0 auto 26px'
            }}
          >
            <div
              tabIndex={0}
              style={metricCardBase}
              onMouseEnter={onCardHoverIn}
              onMouseLeave={onCardHoverOut}
              onFocus={onCardHoverIn}
              onBlur={onCardHoverOut}
            >
              <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>
                <span style={{ fontFamily: 'Alexandria', fontSize: '14px', color: '#E8F5FF', fontWeight: 700 }}>
                  عدد التعليقات
                </span>
                <div style={iconChip}>
                  <MessageSquare size={18} color="#28E3DA" />
                </div>
              </div>

              <div style={{ marginTop: '8px', display: 'flex', flexDirection: 'row', alignItems: 'baseline', justifyContent: 'space-between' }}>
                <span style={{ fontFamily: 'Alexandria', fontWeight: 800, fontSize: '22px', letterSpacing: '0.2px' }}>
                  {n(cards.commentsCount).toLocaleString('en-US')}
                </span>
                <span style={{ fontFamily: 'Alexandria', fontSize: '13px', fontWeight: 800, color: n(cards.commentsDeltaPct) >= 0 ? '#19B228' : '#EC606E' }}>
                  {n(cards.commentsDeltaPct) >= 0 ? '+' : ''}
                  {n(cards.commentsDeltaPct).toLocaleString('en-US')}%
                </span>
              </div>
            </div>

            <div
              tabIndex={0}
              style={metricCardBase}
              onMouseEnter={onCardHoverIn}
              onMouseLeave={onCardHoverOut}
              onFocus={onCardHoverIn}
              onBlur={onCardHoverOut}
            >
              <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>
                <span style={{ fontFamily: 'Alexandria', fontSize: '14px', color: '#E8F5FF', fontWeight: 700 }}>
                  عدد التقييمات
                </span>
                <div style={iconChip}>
                  <BarChart3 size={18} color="#28E3DA" />
                </div>
              </div>

              <div style={{ marginTop: '8px', display: 'flex', flexDirection: 'row', alignItems: 'baseline', justifyContent: 'space-between' }}>
                <span style={{ fontFamily: 'Alexandria', fontWeight: 800, fontSize: '22px', letterSpacing: '0.2px' }}>
                  {n(cards.evaluationsCount).toLocaleString('en-US')}
                </span>
                <span style={{ fontFamily: 'Alexandria', fontSize: '13px', fontWeight: 800, color: n(cards.evaluationsDeltaPct) >= 0 ? '#19B228' : '#EC606E' }}>
                  {n(cards.evaluationsDeltaPct) >= 0 ? '+' : ''}
                  {n(cards.evaluationsDeltaPct).toLocaleString('en-US')}%
                </span>
              </div>
            </div>

            <div
              tabIndex={0}
              style={metricCardBase}
              onMouseEnter={onCardHoverIn}
              onMouseLeave={onCardHoverOut}
              onFocus={onCardHoverIn}
              onBlur={onCardHoverOut}
            >
              <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>
                <span style={{ fontFamily: 'Alexandria', fontSize: '14px', color: '#E8F5FF', fontWeight: 700 }}>
                  عدد المستخدمين
                </span>
                <div style={iconChip}>
                  <Users size={18} color="#28E3DA" />
                </div>
              </div>

              <div style={{ marginTop: '8px', display: 'flex', flexDirection: 'row', alignItems: 'baseline', justifyContent: 'space-between' }}>
                <span style={{ fontFamily: 'Alexandria', fontWeight: 800, fontSize: '22px', letterSpacing: '0.2px' }}>
                  {n(cards.usersCount).toLocaleString('en-US')}
                </span>
                <span style={{ fontFamily: 'Alexandria', fontSize: '13px', fontWeight: 800, color: n(cards.usersDeltaPct) >= 0 ? '#19B228' : '#EC606E' }}>
                  {n(cards.usersDeltaPct) >= 0 ? '+' : ''}
                  {n(cards.usersDeltaPct).toLocaleString('en-US')}%
                </span>
              </div>
            </div>

            <div
              tabIndex={0}
              style={metricCardBase}
              onMouseEnter={onCardHoverIn}
              onMouseLeave={onCardHoverOut}
              onFocus={onCardHoverIn}
              onBlur={onCardHoverOut}
            >
              <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>
                <span style={{ fontFamily: 'Alexandria', fontSize: '14px', color: '#E8F5FF', fontWeight: 700 }}>
                  زيارات الموقع
                </span>
                <div style={iconChip}>
                  <Globe size={18} color="#28E3DA" />
                </div>
              </div>

              <div style={{ marginTop: '8px', display: 'flex', flexDirection: 'row', alignItems: 'baseline', justifyContent: 'space-between' }}>
                <span style={{ fontFamily: 'Alexandria', fontWeight: 800, fontSize: '22px', letterSpacing: '0.2px' }}>
                  {n(cards.pageViews).toLocaleString('en-US')}
                </span>
                <span style={{ fontFamily: 'Alexandria', fontSize: '12px', fontWeight: 800, color: '#C9E7FF' }}>
                  متصل الآن: {n(cards.onlineNow).toLocaleString('en-US')}
                </span>
              </div>
            </div>
          </div>

          <div
            style={{
              width: '100%',
              maxWidth: '1108px',
              margin: '0 auto',
              marginBottom: '28px',
              borderRadius: '20px',
              border: '1px solid rgba(40,227,218,.22)',
              background: 'radial-gradient(55% 100% at 50% -5%,rgba(40,227,218,.14) 0%,rgba(40,227,218,0) 60%),rgba(9,28,43,.9)',
              boxShadow: '0 8px 32px rgba(0,0,0,.35)',
              backdropFilter: 'blur(6px)',
              padding: isMobile ? '16px 14px' : '22px 26px',
              position: 'relative',
              overflow: 'hidden'
            }}
          >
            {loading && (
              <div
                style={{
                  position: 'absolute',
                  inset: 0,
                  background: 'rgba(4,12,22,0.45)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  zIndex: 10,
                  backdropFilter: 'blur(2px)'
                }}
              >
                <div
                  style={{
                    width: 34,
                    height: 34,
                    borderRadius: '999px',
                    border: '3px solid rgba(40,227,218,0.25)',
                    borderTopColor: 'rgba(40,227,218,1)',
                    animation: 'spin 0.9s linear infinite'
                  }}
                />
                <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
              </div>
            )}

            <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px', gap: '8px' }}>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontFamily: 'Alexandria', fontSize: '15px', fontWeight: 800, color: '#E8F5FF' }}>
                  حركة التقييمات و زيارات الموقع
                </div>
                <div style={{ marginTop: '4px', fontFamily: 'Alexandria', fontSize: '12px', fontWeight: 600, color: '#A8C5E6' }}>
                  نظرة على الفترة الأخيرة حسب النطاق الزمني
                </div>
              </div>

              <div style={{ display: 'flex', flexDirection: 'row', gap: '6px' }}>
                {[
                  { key: 'week', label: 'أسبوع' },
                  { key: 'month', label: 'شهر' },
                  { key: 'quarter', label: 'ربع سنة' },
                  { key: 'year', label: 'سنة' }
                ].map((r) => {
                  const active = timeRange === r.key;
                  return (
                    <button
                      key={r.key}
                      onClick={() => handleTimeRangeChange(r.key)}
                      style={pillBtn(active)}
                      onMouseDown={(e) => (e.currentTarget.style.transform = 'scale(0.98)')}
                      onMouseUp={(e) => (e.currentTarget.style.transform = 'scale(1)')}
                      onMouseLeave={(e) => (e.currentTarget.style.transform = 'scale(1)')}
                    >
                      {r.label}
                    </button>
                  );
                })}
              </div>
            </div>

            <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'flex-end', flexWrap: 'wrap', gap: '8px', marginBottom: '10px' }}>
              {[
                { key: 'activity',    color: '#4BE879', label: 'زيارات الموقع',      active: activeSeries.activity },
                { key: 'instructors', color: '#40C2FF', label: 'تقييمات المحاضرين', active: activeSeries.instructors },
                { key: 'courses',     color: '#F6C94C', label: 'تقييمات المقررات',  active: activeSeries.courses },
              ].map(({ key, color, label, active }) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => setActiveSeries((p) => ({ ...p, [key]: !p[key] }))}
                  style={{
                    display: 'flex', alignItems: 'center', gap: '7px',
                    padding: '5px 12px', borderRadius: '999px',
                    border: `1px solid ${active ? color + '60' : 'rgba(255,255,255,.1)'}`,
                    background: active ? color + '15' : 'rgba(255,255,255,.04)',
                    cursor: 'pointer', transition: 'all .2s',
                    fontFamily: 'Alexandria', fontSize: '12px',
                    color: active ? '#E8F5FF' : '#64748b',
                    opacity: 1,
                  }}
                >
                  <span style={{ width: 8, height: 8, borderRadius: '999px', background: active ? color : '#64748b', transition: 'background .2s' }}/>
                  {label}
                </button>
              ))}
            </div>

            <div style={{ width: '100%', position: 'relative', overflow: 'hidden', borderRadius: '14px', background: 'rgba(6,14,26,.85)', border: '1px solid rgba(40,227,218,.10)' }}>
              <svg
                viewBox="0 0 700 360"
                width="100%"
                height={isMobile ? 220 : 290}
                preserveAspectRatio="none"
                style={{ display: 'block', cursor: 'crosshair' }}
                onMouseMove={(e) => {
                  const rect = e.currentTarget.getBoundingClientRect();
                  const mouseX = ((e.clientX - rect.left) / rect.width) * 700;
                  const src = chartData.siteActivity.length ? chartData.siteActivity : chartData.instructorEvaluations.length ? chartData.instructorEvaluations : chartData.courseEvaluations;
                  if (!src.length) return;
                  let nearestIdx = 0, nearestDist = Infinity;
                  src.forEach((p, i) => { const d = Math.abs(CPX(p.x) - mouseX); if (d < nearestDist) { nearestDist = d; nearestIdx = i; } });
                  setChartHoverIdx(nearestIdx);
                }}
                onMouseLeave={() => setChartHoverIdx(null)}
              >
                <defs>
                  <linearGradient id="dg-act" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#4BE879" stopOpacity="0.28"/>
                    <stop offset="100%" stopColor="#4BE879" stopOpacity="0"/>
                  </linearGradient>
                  <linearGradient id="dg-ins" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#40C2FF" stopOpacity="0.22"/>
                    <stop offset="100%" stopColor="#40C2FF" stopOpacity="0"/>
                  </linearGradient>
                  <linearGradient id="dg-crs" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#F6C94C" stopOpacity="0.20"/>
                    <stop offset="100%" stopColor="#F6C94C" stopOpacity="0"/>
                  </linearGradient>
                  <clipPath id="chart-clip">
                    <rect x="60" y="10" width="625" height="300"/>
                  </clipPath>
                </defs>

                {                       }
                {[0,1,2,3,4].map(i => (
                  <line key={i} x1="60" y1={10 + i*75} x2="685" y2={10 + i*75} stroke="rgba(255,255,255,.055)" strokeWidth="1"/>
                ))}

                {                 }
                <line x1="60" y1="310" x2="685" y2="310" stroke="rgba(255,255,255,.08)" strokeWidth="1"/>

                {                                    }
                <g clipPath="url(#chart-clip)">
                  {activeSeries.activity && chartData.siteActivity.length > 1 && (
                    <>
                      <path d={smoothArea(chartData.siteActivity)} fill="url(#dg-act)"/>
                      <path d={smoothPath(chartData.siteActivity)} fill="none" stroke="#4BE879" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </>
                  )}
                  {activeSeries.instructors && chartData.instructorEvaluations.length > 1 && (
                    <>
                      <path d={smoothArea(chartData.instructorEvaluations)} fill="url(#dg-ins)"/>
                      <path d={smoothPath(chartData.instructorEvaluations)} fill="none" stroke="#40C2FF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </>
                  )}
                  {activeSeries.courses && chartData.courseEvaluations.length > 1 && (
                    <>
                      <path d={smoothArea(chartData.courseEvaluations)} fill="url(#dg-crs)"/>
                      <path d={smoothPath(chartData.courseEvaluations)} fill="none" stroke="#F6C94C" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </>
                  )}

                  {                                             }
                  {activeSeries.activity && chartData.siteActivity.map((p, i) => (
                    <circle key={`da-${i}`} cx={CPX(p.x)} cy={CPY(p.y)} r="3" fill="#081423" stroke="#4BE879" strokeWidth="1.5"/>
                  ))}
                  {activeSeries.instructors && chartData.instructorEvaluations.map((p, i) => (
                    <circle key={`di-${i}`} cx={CPX(p.x)} cy={CPY(p.y)} r="2.5" fill="#081423" stroke="#40C2FF" strokeWidth="1.5"/>
                  ))}
                  {activeSeries.courses && chartData.courseEvaluations.map((p, i) => (
                    <circle key={`dc-${i}`} cx={CPX(p.x)} cy={CPY(p.y)} r="2.5" fill="#081423" stroke="#F6C94C" strokeWidth="1.5"/>
                  ))}
                </g>

                {                                      }
                {chartHoverIdx !== null && (() => {
                  const series = [
                    activeSeries.activity   && chartData.siteActivity.length        ? { pts: chartData.siteActivity,        color: '#4BE879' } : null,
                    activeSeries.instructors && chartData.instructorEvaluations.length ? { pts: chartData.instructorEvaluations, color: '#40C2FF' } : null,
                    activeSeries.courses    && chartData.courseEvaluations.length    ? { pts: chartData.courseEvaluations,    color: '#F6C94C' } : null,
                  ].filter(Boolean).filter(s => s.pts[chartHoverIdx]);
                  if (!series.length) return null;
                  const hx = CPX(series[0].pts[chartHoverIdx].x);
                  return (
                    <>
                      <line x1={hx} y1="10" x2={hx} y2="310" stroke="rgba(255,255,255,.18)" strokeWidth="1" strokeDasharray="4 3"/>
                      {series.map(({ pts, color }) => {
                        const hy = CPY(pts[chartHoverIdx].y);
                        return (
                          <g key={color}>
                            <circle cx={hx} cy={hy} r="7" fill={color} opacity="0.2"/>
                            <circle cx={hx} cy={hy} r="4.5" fill={color}/>
                            <circle cx={hx} cy={hy} r="2" fill="#fff"/>
                          </g>
                        );
                      })}
                    </>
                  );
                })()}
              </svg>

              {                                                                     }
              <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', overflow: 'hidden' }}>
                {                   }
                {[0,1,2,3,4].map(i => (
                  <div key={i} style={{
                    position: 'absolute',
                    top: `${((10 + i*75)/360)*100}%`,
                    left: 4,
                    transform: 'translateY(-50%)',
                    fontFamily: 'Alexandria',
                    fontSize: 10,
                    fontWeight: 400,
                    color: 'rgba(148,163,184,.55)',
                    lineHeight: 1,
                    whiteSpace: 'nowrap',
                  }}>
                    {['100%','75%','50%','25%','0%'][i]}
                  </div>
                ))}

                {                   }
                {xAxisLabels.map((lbl, i) => (
                  <div key={i} style={{
                    position: 'absolute',
                    top: `${(330/360)*100}%`,
                    left: `${((60 + (i/6)*625)/700)*100}%`,
                    transform: 'translate(-50%, -50%)',
                    fontFamily: 'Alexandria',
                    fontSize: 10,
                    fontWeight: 400,
                    color: 'rgba(148,163,184,.6)',
                    whiteSpace: 'nowrap',
                    lineHeight: 1,
                  }}>
                    {lbl}
                  </div>
                ))}

                {             }
                {chartHoverIdx !== null && (() => {
                  const series = [
                    activeSeries.activity   && chartData.siteActivity.length        ? { pts: chartData.siteActivity,        color: '#4BE879', label: 'زيارات الموقع' }     : null,
                    activeSeries.instructors && chartData.instructorEvaluations.length ? { pts: chartData.instructorEvaluations, color: '#40C2FF', label: 'تقييمات المحاضرين' } : null,
                    activeSeries.courses    && chartData.courseEvaluations.length    ? { pts: chartData.courseEvaluations,    color: '#F6C94C', label: 'تقييمات المقررات' }   : null,
                  ].filter(Boolean).filter(s => s.pts[chartHoverIdx]);
                  if (!series.length) return null;
                  const hx = CPX(series[0].pts[chartHoverIdx].x);
                  const leftPct = Math.min(Math.max((hx/700)*100, 14), 86);
                  return (
                    <div style={{
                      position: 'absolute',
                      top: '5%',
                      left: `${leftPct}%`,
                      transform: 'translateX(-50%)',
                      background: 'rgba(6,14,26,.97)',
                      border: '1px solid rgba(40,227,218,.45)',
                      borderRadius: 10,
                      padding: '9px 13px',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 6,
                      direction: 'rtl',
                    }}>
                      {series.map(({ pts, color, label }) => {
                        const val = pts[chartHoverIdx]?.value ?? pts[chartHoverIdx]?.v ?? '—';
                        return (
                          <div key={color} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <span style={{ width: 8, height: 8, borderRadius: '50%', background: color, flexShrink: 0 }}/>
                            <span style={{ fontFamily: 'Alexandria', fontSize: 11, fontWeight: 400, color: '#E8F5FF', whiteSpace: 'nowrap' }}>
                              {label}:&nbsp;<strong style={{ fontWeight: 700 }}>{val}</strong>
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  );
                })()}
              </div>
            </div>
          </div>

          <div
            style={{
              display: 'grid',
              gridTemplateColumns: isMobile ? '1fr' : 'repeat(2,minmax(0,1fr))',
              gap: isMobile ? '20px' : '30px',
              width: '100%',
              maxWidth: '1108px',
              margin: '0 auto'
            }}
          >
            <div
              tabIndex={0}
              style={bottomCardBase}
              onMouseEnter={onCardHoverIn}
              onMouseLeave={onCardHoverOut}
              onFocus={onCardHoverIn}
              onBlur={onCardHoverOut}
            >
              <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: '12px' }}>
                <div style={{ textAlign: 'right', flex: 1 }}>
                  <div style={{ fontFamily: 'Alexandria', fontSize: isMobile ? '14px' : '15px', fontWeight: 700, color: '#A9D6FF' }}>
                    عدد المحاضرين
                  </div>
                  <div style={{ marginTop: '4px', fontFamily: 'Alexandria', fontWeight: 900, fontSize: isMobile ? '22px' : '26px', color: '#FFFFFF' }}>
                    {n(cards.lecturersCount).toLocaleString('en-US')}
                  </div>
                </div>

                <div
                  style={{
                    width: 44,
                    height: 44,
                    borderRadius: '999px',
                    background: 'radial-gradient(circle at 30% 0%, rgba(40,227,218,0.7), rgba(8,20,35,0.95))',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0
                  }}
                >
                  <GraduationCap size={22} color="#E8F5FF" />
                </div>
              </div>

              <div style={{ marginTop: '12px', flexGrow: 1, display: 'flex', alignItems: 'flex-end', gap: '10px', direction: 'ltr' }}>
                {[18, 32, 24, 40, 28, 36, 30].map((h, idx) => (
                  <div
                    key={idx}
                    onMouseEnter={onBarHoverIn}
                    onMouseLeave={onBarHoverOut}
                    style={{
                      flex: 1,
                      height: `${h}px`,
                      borderRadius: '999px',
                      background: 'linear-gradient(180deg, rgba(255,255,255,0.92) 0%, rgba(255,255,255,0.06) 100%)',
                      opacity: 0.9,
                      transform: 'translateY(0)',
                      transition: 'transform .20s ease-out'
                    }}
                  />
                ))}
              </div>

              <div style={{ marginTop: '10px', display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', fontFamily: 'Alexandria', fontSize: '12px', color: '#D7E9FF' }}>
                <span>{lastActivityText || 'آخر نشاط غير متوفر'}</span>
                <span style={{ opacity: 0.7 }}>إجمالي المحاضرين في النظام</span>
              </div>
            </div>

            <div
              tabIndex={0}
              style={bottomCardBase}
              onMouseEnter={onCardHoverIn}
              onMouseLeave={onCardHoverOut}
              onFocus={onCardHoverIn}
              onBlur={onCardHoverOut}
            >
              <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: '12px' }}>
                <div style={{ textAlign: 'right', flex: 1 }}>
                  <div style={{ fontFamily: 'Alexandria', fontSize: isMobile ? '14px' : '15px', fontWeight: 700, color: '#A9D6FF' }}>
                    عدد المقررات
                  </div>
                  <div style={{ marginTop: '4px', fontFamily: 'Alexandria', fontWeight: 900, fontSize: isMobile ? '22px' : '26px', color: '#FFFFFF' }}>
                    {n(cards.coursesCount).toLocaleString('en-US')}
                  </div>
                </div>

                <div
                  style={{
                    width: 44,
                    height: 44,
                    borderRadius: '999px',
                    background: 'radial-gradient(circle at 30% 0%, rgba(40,227,218,0.7), rgba(8,20,35,0.95))',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0
                  }}
                >
                  <BookOpen size={22} color="#E8F5FF" />
                </div>
              </div>

              <div style={{ marginTop: '12px', flexGrow: 1, position: 'relative' }}>
                <svg width="100%" height="90" viewBox="0 0 200 80" preserveAspectRatio="none">
                  <path d="M0 45 L30 50 L60 40 L90 55 L120 38 L150 30 L180 42" stroke="#4BE879" strokeWidth="2" fill="none" strokeLinecap="round" />
                  {[{ x: 0, y: 45 }, { x: 30, y: 50 }, { x: 60, y: 40 }, { x: 90, y: 55 }, { x: 120, y: 38 }, { x: 150, y: 30 }, { x: 180, y: 42 }].map((p, i) => (
                    <circle
                      key={i}
                      cx={p.x}
                      cy={p.y}
                      r="3"
                      fill="#081423"
                      stroke="#4BE879"
                      strokeWidth="1.5"
                      style={{ transition: 'transform .20s ease-out' }}
                      onMouseEnter={(e) => (e.currentTarget.style.transform = 'scale(1.3)')}
                      onMouseLeave={(e) => (e.currentTarget.style.transform = 'scale(1)')}
                    />
                  ))}
                </svg>
              </div>

              <div style={{ marginTop: '10px', display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', fontFamily: 'Alexandria', fontSize: '12px', color: '#D7E9FF' }}>
                <span>{lastActivityText || 'آخر نشاط غير متوفر'}</span>
                <span style={{ opacity: 0.7 }}>تنوع المقررات داخل المنصة</span>
              </div>
            </div>
          </div>
        </div>

        <MobileBottomNav />
      </div>
    </div>
  );
};

export default DashboardPage;
